import React, { FC, useEffect, useState } from 'react'

import SelectedEventFeeList from './components/confirmation/SelectedEventFeeList'
import SelectedEventGrandTotal from './components/confirmation/SelectedEventGrandTotal'
import SelectedEventList from './components/confirmation/SelectedEventList'
import { SelectedEventPaperworkList } from './components/confirmation/SelectedEventPaperworkList'
import SelectedEventTicketList from './components/confirmation/SelectedEventTicketList'
import { SplitBill } from './SplitBill'
import ViewsLoader from '../../../../components/loader/ViewsLoader'

import UserService from '../../../../services/firebase-service/users/userService'

import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectRegistration,
  selectRegistrationByDay,
  selectRegistrationFeesR,
  selectRegistrationsByDayInDb,
  selectRegistrationTicketsR,
  updatePayTabGrandTotalAccordion,
} from '../../../../store/registration/registrationSlice'
import { selectedEvent } from '../../../../store/events/eventsSlice'

import { convertPrice } from '../../../../helpers/price'
import { addSalesTax, getCurrency } from '../../../../helpers/payment'
import { getAllItems } from '../../../../helpers/getRegistrations'

import { IRegistrationEventTab } from '../../../../types/competitor_types'
import { IUserInterface } from '../../../../models/users/user.interface'

import { FEES_CATEGORY_CONST } from '../../../../components/events/views/details/EventDetailsViewComponentFees'

interface EventClinicConfirmationTabProps {
  activeTab: IRegistrationEventTab
  setEventTab: (eventTab: IRegistrationEventTab) => void
  setIsSplitBill: (value: boolean) => void
  isSplitBill: boolean
}

const EventClinicConfirmationTab: FC<EventClinicConfirmationTabProps> = ({
  activeTab,
  setEventTab,
  setIsSplitBill,
  isSplitBill,
}) => {
  const dispatch = useAppDispatch()

  const [registrar, setRegistrar] = useState<IUserInterface | null>(null)
  const [loading, setLoading] = useState(false)

  const registration = useAppSelector(selectRegistration)
  const registrationFees = useAppSelector(selectRegistrationFeesR)
  const registrationsClasses = useAppSelector(selectRegistrationByDay)
  const registrationTickets = useAppSelector(selectRegistrationTicketsR)
  const registrationsByDayInDb = useAppSelector(selectRegistrationsByDayInDb)
  const selectedEventR = useAppSelector(selectedEvent)

  const { EventPaymentSettings } = selectedEventR

  useEffect(() => {
    const getRegistrar = async () => {
      if (!registration?.userId) return
      const user = await UserService.getUserById(registration.userId)
      setRegistrar(user)
    }
    getRegistrar().then()
  }, [registration])

  useEffect(() => {
    if (registration?.id) setIsSplitBill(registration?.isSplit || false)
  }, [registration, setIsSplitBill])

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)

      try {
        let registrationsByDay = registrationsClasses.filter((rider) => !rider.isDivision)

        const governanceFeesItems = registrationFees.filter(
          (fee) => fee.feesItemCategory === FEES_CATEGORY_CONST.GOVERNANCE
        )

        const feesItems = registrationFees.filter(
          (fee) => fee.feesItemCategory !== FEES_CATEGORY_CONST.GOVERNANCE
        )

        const ticketsItems = registrationTickets

        let ticketTotal = 0
        let ticketTotalSalesTax = 0
        ticketsItems.forEach((item) => {
          const amount = (item?.selectedUnitsCount || 1) * convertPrice(item?.ticketPrice)
          ticketTotal += amount
          ticketTotalSalesTax += addSalesTax(
            item.created,
            amount,
            item.includeSalesTax,
            selectedEventR?.EventPaymentSettings?.chargeSalesTax
          )
        })

        let feesTotal = 0
        let feesTotalSalesTax = 0
        feesItems.forEach((item) => {
          const amount = (item?.selectedUnitsCount || 1) * convertPrice(item?.feesPrice)
          feesTotal += amount
          feesTotalSalesTax += addSalesTax(
            item.created,
            amount,
            item.includeSalesTax,
            selectedEventR?.EventPaymentSettings?.chargeSalesTax
          )
        })

        let governanceTotal = 0
        let governanceTotalSalesTax = 0
        governanceFeesItems.forEach((item) => {
          const amount = (item?.selectedUnitsCount || 1) * convertPrice(item?.feesPrice)
          governanceTotal += amount
          governanceTotalSalesTax += addSalesTax(
            item.created,
            amount,
            item.includeSalesTax,
            selectedEventR?.EventPaymentSettings?.chargeSalesTax
          )
        })

        let registrationTotal = 0
        let registrationTotalSalesTax = 0
        registrationsByDay.forEach((item) => {
          let amount = 0
          if (item.isQualify) amount += convertPrice(item.qualifyFee)
          if (item.registrationPrice) amount += convertPrice(item.registrationPrice)

          registrationTotal += amount
          registrationTotalSalesTax += addSalesTax(
            item.created,
            amount,
            item.includeSalesTax,
            selectedEventR?.EventPaymentSettings?.chargeSalesTax
          )
        })

        let totalPrice = ticketTotal + registrationTotal + feesTotal + governanceTotal
        const salesTax =
          ticketTotalSalesTax +
          feesTotalSalesTax +
          governanceTotalSalesTax +
          registrationTotalSalesTax

        const { allItems, amountByPerson } = getAllItems({
          registrationsByDay: registrationsByDay,
          fees: [...feesItems, ...governanceFeesItems],
          tickets: ticketsItems,
          chargeSalesTax: selectedEventR?.EventPaymentSettings?.chargeSalesTax || false,
        })

        dispatch(
          updatePayTabGrandTotalAccordion({
            registrationTotal: registrationTotal / 100,
            feesTotal: feesTotal / 100,
            ticketTotal: ticketTotal / 100,
            governanceTotal: governanceTotal / 100,
            totalItemsCount: allItems.length,
            salesTax: salesTax / 100,
            totalPrice: (totalPrice + salesTax) / 100,
            amountByPerson: amountByPerson,
          })
        )
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }
    loadData().then()
  }, [
    registrationsByDayInDb,
    selectedEventR?.EventPaymentSettings?.chargeSalesTax,
    dispatch,
    registrationFees,
    registrationTickets,
    registrationsClasses,
  ])

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center p-4">
        <ViewsLoader />
      </div>
    )
  }

  const currency = getCurrency(selectedEventR?.EventPaymentSettings?.currency)

  return (
    <>
      {EventPaymentSettings?.allowSplitPayment && (
        <SplitBill isSplitBill={isSplitBill} setIsSplitBill={setIsSplitBill} />
      )}
      <SelectedEventList
        activeTab={activeTab}
        setEventTab={setEventTab}
        isSplitBill={isSplitBill}
        registrar={registrar}
        currency={currency}
      />
      <SelectedEventFeeList
        type="fees"
        setEventTab={setEventTab}
        isSplitBill={isSplitBill}
        registrar={registrar}
        currency={currency}
      />
      <SelectedEventFeeList
        type="mandatory-fees"
        setEventTab={setEventTab}
        isSplitBill={isSplitBill}
        registrar={registrar}
        currency={currency}
      />
      <SelectedEventTicketList
        setEventTab={setEventTab}
        isSplitBill={isSplitBill}
        registrar={registrar}
        currency={currency}
      />
      <SelectedEventPaperworkList setEventTab={setEventTab} />
      <SelectedEventGrandTotal isSplitBill={isSplitBill} currency={currency} />
    </>
  )
}

export default EventClinicConfirmationTab
